import { Chip, Tooltip } from "@mui/material";
import type { ChipProps } from "@mui/material";
import type { HealthStatus, PremiseHealth } from "@shared/types/foyer";

/** Traffic-light mapping for the rolled-up premise health verdict. */
const STATUS_META: Record<
  HealthStatus,
  { label: string; color: ChipProps["color"] }
> = {
  healthy: { label: "Healthy", color: "success" },
  warning: { label: "Warning", color: "warning" },
  critical: { label: "Critical", color: "error" },
  unknown: { label: "No devices", color: "default" },
};

/**
 * Green/amber/red premise-health chip. Hovering reveals the underlying reasons
 * (e.g. "2 devices offline", "1 on an old version") so an operator gets the gist
 * without opening the Fleet console.
 */
export default function HealthChip({
  health,
  size = "small",
  testId,
}: {
  health?: PremiseHealth;
  size?: ChipProps["size"];
  testId?: string;
}) {
  const meta = STATUS_META[health?.status ?? "unknown"];
  const reasons = health?.reasons ?? [];
  const tip = reasons.length > 0 ? reasons.join(" · ") : meta.label;
  return (
    <Tooltip title={tip} arrow>
      <Chip
        size={size}
        color={meta.color}
        variant={meta.color === "default" ? "outlined" : "filled"}
        label={meta.label}
        data-testid={testId}
      />
    </Tooltip>
  );
}
